import React, { useMemo } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import BookmarkIcon from '@mui/icons-material/Bookmark';

/**
 * Sidebar Navigation Component
 * Provides navigation between different application modes
 */
const SidebarNavigation = ({ activeMode, onModeChange, activeSubMode, onSubModeChange }) => {
  // Initialize dependencies
  const theme = useTheme();
  const drawerWidth = 240;

  // Define navigation items using useMemo to prevent recreation on each render
  const navItems = useMemo(() => [
    {
      id: 'home',
      label: 'Home',
      icon: <HomeIcon />
    },
    {
      id: 'filter',
      label: 'List Cleaner',
      icon: <PlaylistAddCheckIcon />
    },
    {
      id: 'research',
      label: 'Website Analyzer',
      icon: <SearchIcon />
    },
    {
      id: 'templates',
      label: 'Templates',
      icon: <BookmarkIcon />
    },
    {
      id: 'activity-history',
      label: 'Activity History',
      icon: <HistoryIcon />
    }
  ], []);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { 
          width: drawerWidth, 
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.default,
          borderRight: `1px solid ${theme.palette.divider}`
        },
      }}
    >
      <Box sx={{ 
        height: '140px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        borderBottom: 'none',
        padding: '20px 10px'
      }}>
        <img 
          src="/logo-full.png" 
          alt="Wyspur AI" 
          style={{ 
            height: '182px', 
            width: 'auto',
            objectFit: 'contain',
            maxWidth: '286px'
          }}
        />
      </Box>
      {/* Removed horizontal divider */}
      <List>
        {/* Navigation items list */}
        {navItems && Array.isArray(navItems) ? navItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={activeMode === item.id}
              onClick={() => {
                // Handle normal mode changes
                onModeChange(item.id);
                // Clear sub-mode for all navigation items
                if (onSubModeChange) {
                  onSubModeChange(null);
                }
              }}
              sx={{
                py: 2,
                '&.Mui-selected': {
                  backgroundColor: `${theme.palette.primary.main}20`,
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.primary.main}30`,
                  }
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon sx={{ 
                color: activeMode === item.id ? theme.palette.primary.main : 'inherit'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{
                  fontWeight: activeMode === item.id ? 600 : 400,
                  color: activeMode === item.id ? theme.palette.primary.main : 'inherit',
                  fontSize: '0.9rem'
                }}
              />
            </ListItemButton>
          </ListItem>
        )) : <div>No navigation items available</div>}
      </List>
    </Drawer>
  );
};

export default SidebarNavigation;
